// src/components/game/DragDropGame.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const DragDropGame = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(getScore('dragdrop'));

  const components = [
    { id: 1, name: 'Header', icon: '📋', desc: 'ვებ გვერდის ზედა ნაწილი' },
    { id: 2, name: 'Sidebar', icon: '📌', desc: 'მარცხენა/მარჯვენა პანელი' },
    { id: 3, name: 'Main', icon: '📄', desc: 'მთავარი კონტენტი' },
    { id: 4, name: 'Footer', icon: '🔚', desc: 'ვებ გვერდის ქვედა ნაწილი' },
    { id: 5, name: 'Navbar', icon: '🧭', desc: 'ნავიგაციის ზოლი' },
    { id: 6, name: 'Content', icon: '📝', desc: 'სტატიების სია' }
  ];

  const correctOrder = ['Header', 'Navbar', 'Sidebar', 'Main', 'Content', 'Footer'];
  const [order, setOrder] = useState([...components]);
  const [message, setMessage] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    let timer;
    if (isPlaying && !isCorrect) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, isCorrect]);

  useEffect(() => {
    const shuffled = [...components].sort(() => Math.random() - 0.5);
    setOrder(shuffled);
    setIsPlaying(true);
  }, []);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('index', index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setHoveredIndex(index);
  };

  const handleDragLeave = (e) => {
    setHoveredIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    setHoveredIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('index'));
    if (fromIndex === targetIndex) return;
    
    const newOrder = [...order];
    const [item] = newOrder.splice(fromIndex, 1);
    newOrder.splice(targetIndex, 0, item);
    setOrder(newOrder);
    setDraggedIndex(null);
  };

  const checkOrder = () => {
    const currentOrder = order.map(item => item.name);
    setAttempts(attempts + 1);
    if (JSON.stringify(currentOrder) === JSON.stringify(correctOrder)) {
      setMessage('🎉 სწორია! React გვერდი სწორად ააწყვეთ!');
      setIsCorrect(true);
      setIsPlaying(false);
      const bonus = Math.max(10 - attempts + 1, 1);
      const timeBonus = Math.max(5, 10 - Math.floor(time / 10));
      const newScore = Math.min(20, bonus + timeBonus);
      setScore(newScore);
      if (newScore > bestScore) {
        updateScore('dragdrop', newScore);
        setBestScore(getScore('dragdrop'));
      } else {
        updateScore('dragdrop', newScore);
        setBestScore(getScore('dragdrop'));
      }
    } else {
      setMessage('❌ არასწორია, სცადეთ თავიდან!');
      setIsCorrect(false);
    }
  };

  const reset = () => {
    const shuffled = [...components].sort(() => Math.random() - 0.5);
    setOrder(shuffled);
    setMessage('');
    setIsCorrect(false);
    setAttempts(0);
    setTime(0);
    setIsPlaying(true);
    setScore(0);
  };

  const getPositionColor = (index) => {
    if (isCorrect) {
      const name = order[index]?.name;
      const correctIndex = correctOrder.indexOf(name);
      if (correctIndex === index) return '#4ade80';
      return '#f87171';
    }
    return 'transparent';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: '550px',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(20px, 4vw, 30px)',
    borderRadius: '20px',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes drop {
            0% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          .game-title {
            background: linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .drop-zone {
            animation: drop 0.3s ease-out;
          }
          .drag-item {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .drag-item:hover {
            transform: scale(1.02) translateX(5px);
          }
          .drag-item.dragging {
            opacity: 0.5;
            transform: scale(0.95);
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: '8px 20px',
          marginBottom: '20px',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: '12px',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1 style={{ margin: 0, fontSize: 'clamp(22px, 3vw, 32px)' }}>
            <span className="game-title">🧩 React Layout</span>
          </h1>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666' }}>
              🎯 {score}
            </span>
          </div>
        </div>

        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666',
          fontSize: 'clamp(13px, 1.5vw, 16px)',
          marginBottom: '20px'
        }}>
          გადაიტანეთ კომპონენტები სწორი თანმიმდევრობით
        </p>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
          gap: '8px',
          marginBottom: '20px',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '18px' }}>🔄</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{attempts}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>მცდელობა</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>⏱️</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{formatTime(time)}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
          </div>
          <div>
            <div style={{ fontSize: '18px' }}>📦</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{order.length}</div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>კომპონენტი</div>
          </div>
          <div>
            <div style={{ fontSize: '18px', color: '#FFD93D' }}>⭐</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>
              {score > 0 ? score : '--'}
            </div>
            <div style={{ fontSize: '9px', color: isDarkMode ? '#666' : '#999' }}>ქულა</div>
          </div>
        </div>

        {/* Drop Zone */}
        <div style={{
          margin: '20px 0',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
          borderRadius: '16px',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
          minHeight: '250px'
        }}>
          {order.map((comp, index) => (
            <div
              key={comp.id}
              draggable={!isCorrect}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`drag-item ${draggedIndex === index ? 'dragging' : ''} ${hoveredIndex === index ? 'drop-zone' : ''}`}
              style={{
                padding: 'clamp(14px, 2vw, 18px)',
                margin: '8px 0',
                background: isCorrect 
                  ? getPositionColor(index)
                  : (isDarkMode ? 'rgba(10,10,26,0.8)' : 'rgba(240,240,240,0.8)'),
                borderRadius: '12px',
                cursor: isCorrect ? 'default' : 'grab',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: '2px solid',
                borderColor: hoveredIndex === index 
                  ? '#667eea' 
                  : isCorrect 
                    ? (getPositionColor(index) === '#4ade80' ? '#4ade80' : '#f87171')
                    : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                color: isDarkMode ? '#fff' : '#000',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: hoveredIndex === index ? 'scale(1.02)' : 'scale(1)',
                boxShadow: hoveredIndex === index 
                  ? '0 4px 20px rgba(102,126,234,0.2)' 
                  : 'none',
                opacity: draggedIndex === index ? '0.5' : '1'
              }}
            >
              <span style={{ fontSize: 'clamp(24px, 3vw, 32px)' }}>{comp.icon}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 'clamp(16px, 2vw, 20px)', fontWeight: 'bold' }}>
                  {comp.name}
                </span>
                <p style={{ 
                  fontSize: '11px', 
                  margin: '2px 0 0 0',
                  color: isDarkMode ? '#666' : '#999'
                }}>
                  {comp.desc}
                </p>
              </div>
              {isCorrect && (
                <span style={{ 
                  fontSize: '20px',
                  color: getPositionColor(index) === '#4ade80' ? '#fff' : '#fff'
                }}>
                  {getPositionColor(index) === '#4ade80' ? '✅' : '❌'}
                </span>
              )}
              <span style={{ opacity: 0.3, fontSize: '20px' }}>⋮⋮</span>
            </div>
          ))}
        </div>

        {message && (
          <div style={{
            marginTop: '15px',
            padding: 'clamp(15px, 2vw, 20px)',
            background: isCorrect 
              ? 'linear-gradient(135deg, #4ade80, #22d3ee)' 
              : 'linear-gradient(135deg, #f87171, #ef4444)',
            borderRadius: '12px',
            color: isCorrect ? '#000' : '#fff',
            textAlign: 'center',
            animation: 'slideUp 0.3s ease-out',
            fontWeight: 'bold'
          }}>
            {message}
            {isCorrect && (
              <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.9 }}>
                🎯 ქულა: {score}/20
              </div>
            )}
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginTop: '20px',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={checkOrder} 
            disabled={isCorrect}
            style={{
              flex: 1,
              padding: 'clamp(14px, 2vw, 18px)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              cursor: isCorrect ? 'default' : 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: '0 4px 20px rgba(102,126,234,0.3)',
              opacity: isCorrect ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isCorrect) {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.boxShadow = '0 8px 30px rgba(102,126,234,0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isCorrect) {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 4px 20px rgba(102,126,234,0.3)';
              }
            }}
          >
            ✅ შემოწმება
          </button>
          <button 
            onClick={reset}
            style={{
              flex: 1,
              padding: 'clamp(14px, 2vw, 18px)',
              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: isDarkMode ? '#fff' : '#000',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
              borderRadius: '12px',
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
            }}
          >
            🔄 თავიდან
          </button>
        </div>

        {/* Correct Order Hint */}
        {!isCorrect && attempts > 0 && (
          <div style={{
            marginTop: '15px',
            padding: '12px',
            background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
            borderRadius: '12px',
            fontSize: '12px',
            color: isDarkMode ? '#666' : '#999',
            textAlign: 'center',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
          }}>
            💡 მინიშნება: Header → Navbar → Sidebar → Main → Content → Footer
          </div>
        )}
      </div>
    </div>
  );
};

export default DragDropGame;